import { Command } from 'commander';
import { ConfigService } from '../../services/config.service.js';
import { ImageService } from '../../services/image.service.js';
import { ValidationError } from '../../errors/index.js';
import terminalImage from 'terminal-image';

// Supported image sizes with aspect ratios
const SUPPORTED_SIZES = [
    '1024x1024',  // 1:1 (default)
    '1792x1024',  // 16:9 (landscape)
    '1024x1792',  // 9:16 (portrait)
    '1024x768',   // 4:3 (landscape)
    '768x1024',   // 3:4 (portrait)
] as const;

type ImageSize = typeof SUPPORTED_SIZES[number];

export function createImageGenCommand(): Command {
    return new Command('gen')
        .description('Generate an image from text prompt')
        .argument('<prompt>', 'Image description prompt')
        .option('-m, --model <model>', 'Model to use', 'gemini-3-pro-image-preview')
        .option('-s, --size <size>', `Image size: ${SUPPORTED_SIZES.join(', ')}`, '1024x1024')
        .option('-q, --quality <quality>', 'Image quality: standard, hd', 'hd')
        .option('--no-display', 'Don\'t display image in terminal')
        .action(async (prompt, options) => {
            const configService = new ConfigService();
            const config = await configService.load();

            if (!config) {
                throw new ValidationError('Not initialized. Run "jai1 auth" first.');
            }

            // Validate size
            const size = options.size as ImageSize;
            if (!SUPPORTED_SIZES.includes(size)) {
                console.error(`\n❌ Invalid size: ${size}`);
                console.error(`   Supported sizes: ${SUPPORTED_SIZES.join(', ')}`);
                console.error('\n   Size reference:');
                console.error('     1024x1024 - Square (1:1)');
                console.error('     1792x1024 - Landscape wide (16:9)');
                console.error('     1024x1792 - Portrait tall (9:16)');
                console.error('     1024x768  - Landscape (4:3)');
                console.error('     768x1024  - Portrait (3:4)');
                process.exit(1);
            }

            const imageService = new ImageService();

            console.log('🎨 Generating image...');
            console.log(`   Prompt: ${prompt}`);
            console.log(`   Model: ${options.model}`);
            console.log(`   Size: ${size}`);
            console.log(`   Quality: ${options.quality}`);

            try {
                const result = await imageService.generate(config, {
                    prompt,
                    model: options.model,
                    size,
                    quality: options.quality,
                });

                console.log('\n✅ Image generated successfully!');
                console.log(`   URL: ${result.url}`);
                console.log(`   Size: ${(result.contentSize / 1024).toFixed(2)} KB`);
                console.log(`   ID: ${result.id}`);

                // Display in terminal if supported and not disabled
                if (options.display !== false) {
                    try {
                        const imageResponse = await fetch(result.url);
                        const arrayBuffer = await imageResponse.arrayBuffer();
                        const imageBuffer = Buffer.from(arrayBuffer);
                        const rendered = await terminalImage.buffer(imageBuffer, {
                            width: '50%',
                            height: '50%',
                        });
                        console.log('\n' + rendered);
                    } catch (displayError) {
                        // Terminal image display not supported or failed
                    }
                }

                console.log('\n💡 Tip: Use "jai1 image list" to view all your images');
                console.log('        Use "jai1 image info <id>" for details');

            } catch (error) {
                if (error instanceof Error) {
                    if (error.message.includes('Rate limit')) {
                        console.error('\n⚠️  Rate limit exceeded');
                        console.error('    Use "jai1 image limits" to check your quota');
                    } else {
                        throw error;
                    }
                }
                throw error;
            }
        });
}
